import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { AcuteSymptomsSection } from "./AcuteSymptomsSection";
import { CLINIC_COLORS, BTN_GRADIENT } from "./theme";

const CARD_STYLE = {
  background: "#ffffff",
  borderRadius: "0.75rem",
  boxShadow: "0 4px 20px rgba(0,101,141,0.06)",
  border: "1px solid rgba(189,200,209,0.15)",
};

const FILLED_INPUT_STYLE = {
  background: "#f1f4f9",
  border: "2px solid transparent",
  transition: "border-color 0.2s, background-color 0.2s",
};

export function SymptomsStep({
  complaints,
  onComplaintsChange,
  questionnaire,
  updateQuestionnaire,
  testValues,
  handleInputChange,
  hasAcuteSymptoms,
  onContinue,
  error,
  isDoctorView = false,
}) {
  const { t } = useTranslation();

  return (
    <div className="animate-fade-in space-y-6">
      {error && (
        <div
          className="text-sm p-4 rounded-xl"
          style={{ background: CLINIC_COLORS.warmAmberBg, color: CLINIC_COLORS.warmAmber }}
          data-testid="symptoms-error"
        >
          {error}
        </div>
      )}

      {/* Complaints card */}
      <div className="p-8" style={CARD_STYLE} data-testid={isDoctorView ? "symptoms-card" : "complaints-card"}>
        <label
          htmlFor={isDoctorView ? "doctor-complaints" : "complaints"}
          className="font-medium text-sm block mb-3"
          style={{ color: CLINIC_COLORS.muted }}
        >
          {t("dashboard.complaintsLabel")}
        </label>
        <textarea
          id={isDoctorView ? "doctor-complaints" : "complaints"}
          data-testid={isDoctorView ? "doctor-input-complaints" : "input-complaints"}
          className="w-full min-h-[160px] p-4 rounded-xl resize-y text-base focus:outline-none"
          style={FILLED_INPUT_STYLE}
          onFocus={(e) => {
            e.target.style.borderColor = CLINIC_COLORS.primary;
            e.target.style.background = "#ffffff";
          }}
          onBlur={(e) => {
            e.target.style.borderColor = "transparent";
            e.target.style.background = "#f1f4f9";
          }}
          placeholder={t("dashboard.complaintsPlaceholder")}
          value={complaints}
          onChange={(e) => onComplaintsChange(e.target.value)}
        />
        <p className="text-sm italic mt-2" style={{ color: CLINIC_COLORS.subtle }}>
          {t("dashboard.complaintsOptional")}
        </p>
      </div>

      {/* Acute symptoms card */}
      <div className="p-8" style={CARD_STYLE}>
        <AcuteSymptomsSection
          questionnaire={questionnaire}
          updateQuestionnaire={updateQuestionnaire}
          testValues={testValues}
          handleInputChange={handleInputChange}
          hasAcuteSymptoms={hasAcuteSymptoms}
          isDoctorView={isDoctorView}
        />
      </div>

      {/* Footer actions */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-4">
        <button
          type="button"
          data-testid={isDoctorView ? "doctor-skip-complaints-btn" : "skip-complaints-btn"}
          onClick={onContinue}
          className="order-2 md:order-1 px-8 py-3 font-semibold rounded-full transition-all"
          style={{ color: CLINIC_COLORS.muted, background: "transparent" }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#ebeef3")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          {t("dashboard.skipComplaints")}
        </button>
        <button
          type="button"
          data-testid={isDoctorView ? "continue-to-history-btn" : "continue-complaints-btn"}
          onClick={onContinue}
          className="order-1 md:order-2 w-full md:w-auto px-12 py-4 text-white font-bold rounded-full shadow-lg transition-all hover:scale-[1.02] active:scale-95 inline-flex items-center justify-center gap-2"
          style={{ background: BTN_GRADIENT, boxShadow: "0 8px 20px rgba(0,101,141,0.25)" }}
        >
          {t("dashboard.continueWithComplaints") || t("common.continue") || "Continue"}
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
